'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { authService } from '@/lib/authService';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { doesRealtimeMessageAffectScope } from '../../utils/realtimeScope';
import type { CaptureFormData } from './captureSchema';
import { toRecordString } from './captureSchema';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useVehicleInfo');

export interface VehicleInfo {
  fuelType: string | null;
  isTracked: boolean;
  trackingProvider: string | null;
  liveOdometerKm: number | null;
  storedOdometerKm: number | null;
}

export interface UseVehicleInfoParams {
  isOpen: boolean;
  entryType: string | null | undefined;
  effectiveVehicleId: string | null | undefined;
  workItemId: string;
  vehicleId?: string | null | undefined;
  realtimeClientId: string | null;
  setFormData: React.Dispatch<React.SetStateAction<CaptureFormData>>;
}

export interface UseVehicleInfoReturn {
  vehicleInfo: VehicleInfo | null;
  setVehicleInfo: React.Dispatch<React.SetStateAction<VehicleInfo | null>>;
  fetchVehicleInfo: () => Promise<void>;
}

export function useVehicleInfo({
  isOpen,
  entryType,
  effectiveVehicleId,
  workItemId,
  vehicleId,
  realtimeClientId,
  setFormData,
}: UseVehicleInfoParams): UseVehicleInfoReturn {
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo | null>(null);
  const realtimeVehicleRefreshTimeoutRef = useRef<number | null>(null);

  const fetchVehicleInfo = useCallback(async () => {
    if (!isOpen || !entryType || !['fuel', 'maintenance'].includes(entryType) || !effectiveVehicleId) {
      if (!isOpen) setVehicleInfo(null);
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams({ action: 'get_vehicle_info', vehicle_id: effectiveVehicleId });
      const resp = await fetch(`/api/v1/work-items/fleet?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.success && data.vehicle) {
        const info: VehicleInfo = {
          fuelType: data.vehicle.fuel_type || null,
          isTracked: data.vehicle.is_tracked || false,
          trackingProvider: data.vehicle.tracking_provider || null,
          liveOdometerKm: data.vehicle.live_odometer_km ?? null,
          storedOdometerKm: data.vehicle.stored_odometer_km ?? null,
        };
        setVehicleInfo(info);
        setFormData(prev => {
          const prevRecord = toRecordString(prev);
          const updates: Record<string, string> = {};
          if (info.fuelType && !prevRecord.fuel_type) {
            updates.fuel_type = info.fuelType;
          }
          if (!prevRecord.odometer) {
            const odomKm = info.isTracked
              ? (info.liveOdometerKm ?? info.storedOdometerKm)
              : info.storedOdometerKm;
            if (odomKm != null) {
              updates.odometer = String(odomKm);
            }
          }
          return { ...prev, ...updates } as CaptureFormData;
        });
      }
    } catch (err) {
      if (!isSilentFetchError(err)) logger.warn('[ChildEntryForm] Failed to fetch vehicle info:', err);
    }
  }, [entryType, isOpen, effectiveVehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch, not synchronous setState
    void fetchVehicleInfo();
  }, [fetchVehicleInfo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (realtimeVehicleRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeVehicleRefreshTimeoutRef.current);
        realtimeVehicleRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  const handleRealtimeChildEntryMessage = useCallback((data: unknown) => {
    if (!isOpen || !entryType || !['fuel', 'maintenance'].includes(entryType) || !vehicleId) {
      return;
    }

    if (!doesRealtimeMessageAffectScope(data, {
      workItemIds: [workItemId],
      appointmentIds: [workItemId],
      jobIds: [workItemId],
      entityIds: [vehicleId, workItemId],
      extraIds: [vehicleId],
    })) {
      return;
    }

    if (realtimeVehicleRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeVehicleRefreshTimeoutRef.current);
    }

    realtimeVehicleRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeVehicleRefreshTimeoutRef.current = null;
      void fetchVehicleInfo();
    }, 120);
  }, [entryType, fetchVehicleInfo, isOpen, vehicleId, workItemId]);

  useWebSocketChannel({
    channel: realtimeClientId ? `operations:client:${realtimeClientId}` : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: handleRealtimeChildEntryMessage,
  });

  return {
    vehicleInfo,
    setVehicleInfo,
    fetchVehicleInfo,
  };
}
